/**
 * Environment configuration
 * Centralizes access to environment variables with type safety.
 *
 * Priority order:
 * 1. window.__RUNTIME_CONFIG__ (injected at container start via env-config.sh)
 * 2. import.meta.env (Vite build-time env vars)
 * 3. Defaults
 */

// Runtime config injected by env-config.sh in the Docker container
declare global {
  interface Window {
    __RUNTIME_CONFIG__?: {
      VITE_API_URL?: string;
      VITE_ENABLE_AI_FEATURES?: string;
      VITE_ENABLE_GRAPH_FEATURES?: string;
      VITE_ENABLE_DEBUG?: string;
      VITE_ENVIRONMENT?: string;
      VITE_KEYCLOAK_ISSUER?: string;
      VITE_KEYCLOAK_CLIENT_ID?: string;
      VITE_KEYCLOAK_REDIRECT_URI?: string;
      VITE_KEYCLOAK_SCOPE?: string;
      VITE_SCIENTIFIC_AGENT_URL?: string;
    };
  }
}

function getRuntimeConfig(key: string, fallback?: string): string | undefined {
  // Runtime config takes priority (set at container start)
  const runtimeValue = window.__RUNTIME_CONFIG__?.[key as keyof NonNullable<typeof window.__RUNTIME_CONFIG__>];
  if (runtimeValue) return runtimeValue;

  // Build-time env vars (Vite)
  const buildValue = import.meta.env[key];
  if (buildValue) return buildValue;

  return fallback;
}

export const env = {
  // API Configuration
  // Default to '' (empty) — frontend endpoints already include /api/ prefix
  // In production, nginx proxies /api/* to the backend internal URL
  // In development, set VITE_API_URL to 'http://localhost:3001' for direct backend access
  apiUrl: getRuntimeConfig('VITE_API_URL', '') || '',
  apiTimeout: Number(getRuntimeConfig('VITE_API_TIMEOUT') || '30000'),

  // Keycloak (authorization code + PKCE, public client — no secret here).
  // The realm issuer, WITHOUT a trailing slash. Empty disables login entirely
  // (the auth page says so) rather than redirecting to a broken URL.
  keycloakIssuer: (getRuntimeConfig('VITE_KEYCLOAK_ISSUER', '') || '').replace(/\/$/, ''),
  keycloakClientId: getRuntimeConfig('VITE_KEYCLOAK_CLIENT_ID', 'onto-frontend') || 'onto-frontend',
  // Defaults to `${window.location.origin}/auth/callback` at call time; only set
  // this when the app is served from a path the origin cannot infer.
  keycloakRedirectUri: getRuntimeConfig('VITE_KEYCLOAK_REDIRECT_URI', '') || '',
  keycloakScope: getRuntimeConfig('VITE_KEYCLOAK_SCOPE', 'openid profile email') || 'openid profile email',

  // Feature Flags
  enableAiFeatures: getRuntimeConfig('VITE_ENABLE_AI_FEATURES', 'true') === 'true',
  enableGraphFeatures: getRuntimeConfig('VITE_ENABLE_GRAPH_FEATURES', 'false') === 'true',

  // Development
  enableDebug: getRuntimeConfig('VITE_ENABLE_DEBUG', 'false') === 'true',
  isDevelopment: import.meta.env.DEV,
  isProduction: import.meta.env.PROD,

  // Deployment environment label (development | staging | production)
  // Empty/unset = production (no badge shown). Used by the AppHeader to display
  // an environment tag next to the product name so DEV/staging are easy to spot.
  environment: getRuntimeConfig('VITE_ENVIRONMENT', '') || '',

  // Cross-app navigation: the deployed Scientific Agent chat UI. Both apps
  // share the same Keycloak realm, so a plain href across origins stays
  // authenticated — no shared package, no token handoff required.
  scientificAgentUrl:
    getRuntimeConfig('VITE_SCIENTIFIC_AGENT_URL', 'https://scientificagent.science-next.io') ||
    'https://scientificagent.science-next.io',
} as const;

// Log configuration in development
if (env.isDevelopment && env.enableDebug) {
  console.log('Environment configuration:', {
    apiUrl: env.apiUrl,
    apiTimeout: env.apiTimeout,
    enableAiFeatures: env.enableAiFeatures,
    enableGraphFeatures: env.enableGraphFeatures,
  });
}

// Made with Bob
